// Title helpers for the app-wide document.title (see PageTitle.tsx + usePageTitle).
// The entity title FORMAT intentionally mirrors the server's social-card titles in
// CSFBL.Services/Domain/PreviewService.cs (PageLabels + LabelSuffix) so the browser
// tab, browser history/search suggestions, and link-unfurl cards all read the same.
// Keep the PAGE_LABELS map below in sync with PreviewService.PageLabels.

const PAGE_LABELS: Record<string, string> = {
  // --- in sync with PreviewService.PageLabels ---
  teamstats: 'Team Stats',
  gamelog: 'Game Log',
  freeagents: 'Free Agents',
  depthchart: 'Depth Chart',
  battingorder: 'Batting Order',
  pitchingroles: 'Pitching Roles',
  draftlottery: 'Draft Lottery',
  alltimeleaders: 'All-Time Leaders',
  seasonleaders: 'Season Leaders',
  halloffame: 'Hall of Fame',
  positionchanges: 'Position Changes',
  draftlist: 'Draft List',
  trades: 'Trades',
  advanceddefense: 'Advanced Defense',
  // --- extra tab segments the SPA has that the preview dict didn't enumerate;
  //     the default (capitalize the segment) is fine for the rest ---
  seasonstats: 'Stats',
  playoffstats: 'Playoff Stats',
  splitstats: 'Splits',
  achievementsawards: 'Achievements & Awards',
  drafthistory: 'Draft History',
  commishtools: 'Commissioner Tools',
  teamownership: 'Team Ownership',
  draftreorder: 'Draft Order',
  lowminorscheck: 'Low Minors Check',
}

/**
 * The trailing " {Page Label}" for an entity sub-page, or "" for the bare entity
 * view ("view" / undefined). Mapped multi-word labels win; otherwise the segment
 * is capitalized. Mirrors PreviewService.LabelSuffix.
 */
export function tabSuffix(seg?: string): string {
  if (!seg || seg === 'view') return ''
  const mapped = PAGE_LABELS[seg]
  return ' ' + (mapped ?? seg.charAt(0).toUpperCase() + seg.slice(1))
}

// Static (non-entity) routes → their title. First match wins, so more specific
// patterns come first. A route absent here (help/*, game/*, 404) returns
// `undefined`, which tells PageTitle to leave the title alone so that page's own
// mechanism (or the default) governs — never fight another title owner.
const STATIC_TITLES: Array<[RegExp, string]> = [
  [/^\/user\/referral\/?$/, 'Refer a Friend'],
  [/^\/user\/[^/]+\/records\/?$/, 'Records'],
  [/^\/user\/[^/]+\/timeline\/?$/, 'Timeline'],
  [/^\/user\/[^/]+\/teamrequests\/?$/, 'Team Requests'],
  [/^\/user\/[^/]+\/settings\/?$/, 'Settings'],
  [/^\/user\/(settings|password)\b/, 'Settings'],
  [/^\/user\/[^/]+\/?$/, 'Dashboard'],
  [/^\/privacy\/?$/, 'Privacy Policy'],
  [/^\/supportus\/?$/, 'Support Us'],
  [/^\/signup\/register\/?$/, 'Register'],
  [/^\/signup\/success\/?$/, 'Welcome'],
  [/^\/signup\/?$/, 'Create Your Team'],
  [/^\/teams\/create\/?$/, 'Create a Team'],
  [/^\/teams\/?$/, 'Find a Team'],
  [/^\/leagues\/?$/, 'Find a League'],
  [/^\/users\/?$/, 'Find a Manager'],
  [/^\/admin\/announcements\/?$/, 'Announcements'],
  [/^\/admin\/user-lookup\/?$/, 'User Lookup'],
  [/^\/admin\/reverse-trade\/?$/, 'Reverse Trade'],
  [/^\/admin\/retire-player\/?$/, 'Retire Player'],
  [/^\/admin\/ban-unban-user\/?$/, 'Ban / Unban User'],
  [/^\/admin\/swap-team-divisions\/?$/, 'Swap Team Divisions'],
  [/^\/admin\/rename-team\/?$/, 'Rename Team'],
  [/^\/admin\/create-free-agents\/?$/, 'Create Free Agents'],
  [/^\/admin\/?$/, 'Admin'],
]

/**
 * Title for a static route, or `undefined` if this path isn't a PageTitle-owned
 * static route (entity routes, help/*, game/*, 404 — handled elsewhere or left
 * to the page). Query/hash are ignored by the caller (it passes pathname).
 */
export function staticTitle(pathname: string): string | undefined {
  for (const [rx, title] of STATIC_TITLES) {
    if (rx.test(pathname)) return title
  }
  return undefined
}
